import React from 'react';
import { Search, ArrowUpDown, ArrowUp, ArrowDown, Plus, X, ListFilter } from 'lucide-react';
import { CustomSelect } from './CustomSelect';

export interface SortOption {
  value: string;
  label: string;
}

export interface PageSizeOption {
  value: string | number;
  label: string;
}

interface ToolbarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  searchPlaceholder?: string;

  sortBy?: string;
  onSortByChange?: (val: string) => void;
  sortOptions?: SortOption[];

  sortOrder?: 'asc' | 'desc';
  onSortOrderToggle?: () => void;

  pageSize?: string | number;
  onPageSizeChange?: (size: string) => void;
  pageSizeOptions?: PageSizeOption[];

  actionLabel?: string;
  actionIcon?: React.ComponentType<{ className?: string }>;
  onActionClick?: () => void;

  children?: React.ReactNode;
}

export function Toolbar({
  searchQuery,
  onSearchChange,
  searchPlaceholder = 'Buscar...',
  sortBy,
  onSortByChange,
  sortOptions,
  sortOrder = 'asc',
  onSortOrderToggle,
  pageSize,
  onPageSizeChange,
  pageSizeOptions,
  actionLabel,
  actionIcon: ActionIcon = Plus,
  onActionClick,
  children,
}: ToolbarProps) {
  const hasSortOptions = Boolean(sortBy && onSortByChange && sortOptions && sortOptions.length > 0);
  const hasPageSizes = Boolean(pageSize !== undefined && onPageSizeChange && pageSizeOptions);

  return (
    <div className="flex flex-col gap-3 bg-surface p-3.5 sm:p-4 rounded-2xl border border-border shadow-xs transition-all relative overflow-visible z-20">
      {/* ── BLOCK 1: Search Bar & Primary CTA (Full Width Search on Mobile) ── */}
      <div className="flex items-center gap-2.5 w-full">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full h-10 rounded-xl border border-border bg-surface pl-10 pr-9 text-xs sm:text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20 transition-all shadow-xs"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-lg text-text-tertiary hover:bg-surface-tertiary hover:text-text-primary transition-colors cursor-pointer"
              title="Limpiar búsqueda"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Action CTA Button */}
        {actionLabel && onActionClick && (
          <button
            type="button"
            onClick={onActionClick}
            className="btn-press flex items-center justify-center gap-2 h-10 px-3.5 sm:px-4 text-xs font-bold bg-brand-orange text-white rounded-xl hover:bg-brand-orange-light shadow-md shrink-0 cursor-pointer"
          >
            <ActionIcon className="h-4 w-4" />
            <span className="hidden sm:inline">{actionLabel}</span>
          </button>
        )}
      </div>

      {/* ── BLOCK 2: Contextual Business Filters (Clean Flex Wrap) & Desktop Page Controls ── */}
      {(children || hasPageSizes) && (
        <div className="flex flex-wrap items-center justify-between gap-2.5">
          {/* Custom Page Filters (Branch Toggle, Branch Select, etc.) */}
          {children && (
            <div className="flex flex-wrap items-center gap-2.5 flex-1 min-w-[200px]">
              {children}
            </div>
          )}

          {/* Page Size Selector (Desktop View) */}
          {hasPageSizes && (
            <div className="hidden md:block shrink-0">
              <CustomSelect
                value={pageSize!}
                onChange={onPageSizeChange!}
                options={pageSizeOptions!.map((opt) => ({
                  value: opt.value,
                  label: opt.label,
                }))}
                icon={<ListFilter className="h-3.5 w-3.5 text-text-tertiary" />}
                buttonClassName="!h-10 font-medium text-xs"
                className="w-36 shrink-0"
                align="right"
              />
            </div>
          )}
        </div>
      )}

      {/* ── BLOCK 3: Mobile Card Controls ONLY (Hidden on Desktop >=768px) ── */}
      {(hasSortOptions || hasPageSizes) && (
        <div className="flex md:hidden items-center justify-between gap-2 pt-2 border-t border-border/40">
          {/* Mobile Sort Pill (Crucial for mobile card layout) */}
          {hasSortOptions ? (
            <div className="flex items-center rounded-xl border border-border bg-surface shadow-xs shrink-0 focus-within:ring-2 focus-within:ring-brand-sky/20">
              <CustomSelect
                value={sortBy!}
                onChange={onSortByChange!}
                options={sortOptions!}
                icon={<ArrowUpDown className="h-3.5 w-3.5 text-brand-orange shrink-0" />}
                buttonClassName="!border-0 !rounded-l-xl !rounded-r-none !bg-transparent !h-9 !px-2 font-medium text-xs text-text-secondary"
                className="w-auto min-w-[100px]"
              />
              {onSortOrderToggle && (
                <>
                  <div className="h-4 w-px bg-border shrink-0" />
                  <button
                    type="button"
                    onClick={onSortOrderToggle}
                    className="btn-press flex items-center gap-1 h-9 px-2 text-xs font-bold text-brand-orange hover:bg-surface-tertiary rounded-r-xl transition-colors cursor-pointer shrink-0"
                    title={`Orden: ${sortOrder === 'asc' ? 'Ascendente (A-Z)' : 'Descendente (Z-A)'}`}
                  >
                    {sortOrder === 'asc' ? (
                      <ArrowUp className="h-3.5 w-3.5 text-brand-orange" />
                    ) : (
                      <ArrowDown className="h-3.5 w-3.5 text-brand-orange" />
                    )}
                    <span className="uppercase text-[10px] font-extrabold tracking-wider">{sortOrder}</span>
                  </button>
                </>
              )}
            </div>
          ) : null}

          {/* Mobile Page Size Dropdown */}
          {hasPageSizes && (
            <CustomSelect
              value={pageSize!}
              onChange={onPageSizeChange!}
              options={pageSizeOptions!.map((opt) => ({
                value: opt.value,
                label: opt.label,
              }))}
              icon={<ListFilter className="h-3.5 w-3.5 text-text-tertiary" />}
              buttonClassName="!h-9 font-medium text-xs"
              className="w-32 shrink-0"
              align="right"
            />
          )}
        </div>
      )}
    </div>
  );
}
